import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronDown, X, SlidersHorizontal, Check } from 'lucide-react';
import type { CampaignFilters, CampaignStatus } from '../types/campaign';
import { useLanguage } from '../contexts/LanguageContext';
import { ViewToggle, type ViewMode } from './ViewToggle';

type CampaignModeFilter = 'all' | 'brand_action' | 'influencer_marketing' | 'sponsorship' | 'projecto' | 'services';

interface StatusCount {
  status: CampaignStatus;
  count: number;
  label: string;
}

interface FilterToolbarProps {
  campaignModeFilter: CampaignModeFilter;
  onCampaignModeChange: (mode: CampaignModeFilter) => void;
  selectedStatuses: CampaignStatus[];
  onStatusChange: (statuses: CampaignStatus[]) => void;
  statusCounts: StatusCount[];
  filters: CampaignFilters;
  onFilterChange: (filters: CampaignFilters) => void;
  totalCampaigns: number;
  filteredCount: number;
  uniqueYears: number[];
  uniqueBrands: string[];
  uniqueInfluencers: string[];
  uniqueAgents: string[];
  modeCounts: { all: number; brandAction: number; influencerMarketing: number; sponsorship: number; services: number; projecto: number };
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  showViewToggle?: boolean;
  canViewOthersCampaigns?: boolean;
}

export function FilterToolbar({
  campaignModeFilter,
  onCampaignModeChange,
  selectedStatuses,
  onStatusChange,
  statusCounts,
  filters,
  onFilterChange,
  totalCampaigns,
  filteredCount,
  uniqueYears,
  uniqueBrands,
  uniqueInfluencers,
  uniqueAgents,
  modeCounts,
  viewMode,
  onViewModeChange,
  showViewToggle = false,
  canViewOthersCampaigns = true,
}: FilterToolbarProps) {
  const { t, ts } = useLanguage();
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [filtersDropdownOpen, setFiltersDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const filtersDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
      if (filtersDropdownRef.current && !filtersDropdownRef.current.contains(event.target as Node)) {
        setFiltersDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusToggle = (status: CampaignStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusChange(selectedStatuses.filter(s => s !== status));
    } else {
      onStatusChange([...selectedStatuses, status]);
    }
  };

  const handleSearchChange = (value: string) => {
    onFilterChange({ ...filters, search: value || undefined });
  };

  const handleChange = (key: keyof CampaignFilters, value: string) => {
    let parsedValue: string | number | undefined = value === '' ? undefined : value;
    if (key === 'year' && value !== '') {
      parsedValue = parseInt(value, 10);
    }
    onFilterChange({ ...filters, [key]: parsedValue });
  };

  const activeFilterCount = Object.entries(filters).filter(
    ([key, value]) => key !== 'search' && value !== undefined
  ).length;

  const modeItems = [
    { id: 'all' as const, label: t('filter.all'), count: modeCounts.all },
    { id: 'brand_action' as const, label: t('filter.brandAction'), count: modeCounts.brandAction },
    { id: 'influencer_marketing' as const, label: t('filter.influencerMarketing'), count: modeCounts.influencerMarketing },
    { id: 'sponsorship' as const, label: t('filter.sponsorship'), count: modeCounts.sponsorship },
    { id: 'services' as const, label: t('filter.services'), count: modeCounts.services },
    { id: 'projecto' as const, label: t('filter.projecto'), count: modeCounts.projecto },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 mb-6">
      <div className="flex items-center border-b border-gray-100 overflow-x-auto scrollbar-thin">
        {modeItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onCampaignModeChange(item.id)}
            className={`relative px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
              campaignModeFilter === item.id
                ? 'text-gray-900'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              {item.label}
              <span className={`px-1.5 py-0.5 text-xs rounded-full transition-colors duration-200 ${
                campaignModeFilter === item.id
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {item.count}
              </span>
            </span>
            {campaignModeFilter === item.id && (
              <motion.span
                layoutId="mode-tab-underline"
                className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-accent-500"
                transition={{ type: 'spring', stiffness: 500, damping: 40 }}
              />
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 p-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={filters.search || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('filter.searchPlaceholder')}
            className="w-full pl-10 pr-8 py-2.5 bg-gray-50 border border-transparent rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:bg-white focus:border-green-200 transition-all"
          />
          {filters.search && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
        </div>

        <div className="relative" ref={statusDropdownRef}>
          <button
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all border ${
              selectedStatuses.length > 0
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            {t('filter.status')}
            {selectedStatuses.length > 0 && (
              <span className="flex items-center justify-center w-5 h-5 bg-white text-gray-900 text-xs font-bold rounded-full">
                {selectedStatuses.length}
              </span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {statusDropdownOpen && (
            <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-xl ring-1 ring-gray-100 border-0 py-2 z-50">
              <div className="px-3 py-2 border-b border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('filter.selectStatuses')}
                </p>
              </div>
              {statusCounts.map(({ status, count, label }) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => handleStatusToggle(status)}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors w-full text-left"
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedStatuses.includes(status)
                        ? 'bg-gray-900 border-gray-900'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {selectedStatuses.includes(status) && (
                      <Check className="w-3.5 h-3.5 text-white" />
                    )}
                  </div>
                  <span className="flex-1 text-sm text-gray-700">{label}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {count}
                  </span>
                </button>
              ))}
              {selectedStatuses.length > 0 && (
                <div className="px-3 pt-2 mt-1 border-t border-gray-100">
                  <button
                    onClick={() => onStatusChange([])}
                    className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {t('filter.clearSelection')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="relative" ref={filtersDropdownRef}>
          <button
            onClick={() => setFiltersDropdownOpen(!filtersDropdownOpen)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all border ${
              activeFilterCount > 0
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {t('filter.filters')}
            {activeFilterCount > 0 && (
              <span className="flex items-center justify-center w-5 h-5 bg-gray-800 text-white text-xs font-bold rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          {filtersDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-[480px] bg-white rounded-2xl shadow-xl ring-1 ring-gray-100 border-0 p-4 z-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">{t('filter.advancedFilters')}</h3>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => onFilterChange({})}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {t('filter.clear')}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.year')}</label>
                  <select
                    value={filters.year || ''}
                    onChange={(e) => handleChange('year', e.target.value)}
                    className="w-full px-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-full focus:ring-2 focus:ring-green-400 focus:border-green-200 focus:bg-white transition-colors"
                  >
                    <option value="">{t('filter.allYears')}</option>
                    {uniqueYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.brand')}</label>
                  <select
                    value={filters.brand || ''}
                    onChange={(e) => handleChange('brand', e.target.value)}
                    className="w-full px-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-full focus:ring-2 focus:ring-green-400 focus:border-green-200 focus:bg-white transition-colors"
                  >
                    <option value="">{t('filter.allBrands')}</option>
                    {uniqueBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.influencer')}</label>
                  <select
                    value={filters.influencer || ''}
                    onChange={(e) => handleChange('influencer', e.target.value)}
                    className="w-full px-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-full focus:ring-2 focus:ring-green-400 focus:border-green-200 focus:bg-white transition-colors"
                  >
                    <option value="">{t('filter.allInfluencers')}</option>
                    {uniqueInfluencers.map(influencer => (
                      <option key={influencer} value={influencer}>{influencer}</option>
                    ))}
                  </select>
                </div>

                {canViewOthersCampaigns && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.agent')}</label>
                    <select
                      value={filters.agent_name || ''}
                      onChange={(e) => handleChange('agent_name', e.target.value)}
                      className="w-full px-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-full focus:ring-2 focus:ring-green-400 focus:border-green-200 focus:bg-white transition-colors"
                    >
                      <option value="">{t('filter.allAgents')}</option>
                      {uniqueAgents.map(agent => (
                        <option key={agent} value={agent}>{agent}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.companyInvoice')}</label>
                  <select
                    value={filters.company_invoice_status || ''}
                    onChange={(e) => handleChange('company_invoice_status', e.target.value)}
                    className="w-full px-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-full focus:ring-2 focus:ring-green-400 focus:border-green-200 focus:bg-white transition-colors"
                  >
                    <option value="">{t('filter.allStatuses')}</option>
                    <option value="Not Issued">{ts('Not Issued')}</option>
                    <option value="Issued">{ts('Issued')}</option>
                    <option value="Paid">{ts('Paid')}</option>
                    <option value="Overdue">{ts('Overdue')}</option>
                    <option value="Cancelled">{ts('Cancelled')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.paymentStatus')}</label>
                  <select
                    value={filters.client_payment_status || ''}
                    onChange={(e) => handleChange('client_payment_status', e.target.value)}
                    className="w-full px-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-full focus:ring-2 focus:ring-green-400 focus:border-green-200 focus:bg-white transition-colors"
                  >
                    <option value="">{t('filter.allStatuses')}</option>
                    <option value="Unpaid">{ts('Unpaid')}</option>
                    <option value="Partially Paid">{ts('Partially Paid')}</option>
                    <option value="Paid">{ts('Paid')}</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.invoiceNumber')}</label>
                  <input
                    type="text"
                    value={filters.invoice_number || ''}
                    onChange={(e) => handleChange('invoice_number', e.target.value)}
                    placeholder={t('filter.invoiceNumberPlaceholder')}
                    className="w-full px-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-full focus:ring-2 focus:ring-green-400 focus:border-green-200 focus:bg-white transition-colors"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="ml-auto flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {filteredCount} {t('common.of')} {totalCampaigns}
          </span>
          {showViewToggle && viewMode && onViewModeChange && (
            <ViewToggle currentView={viewMode} onViewChange={onViewModeChange} />
          )}
        </div>
      </div>
    </div>
  );
}
